import React from 'react';
import { FiDroplet, FiChevronRight } from 'react-icons/fi';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { getPhColor, getOrpColorByDesired } from '@/utils/chemistryUtils';

interface ChemistryCardProps {
  mode?: 'light' | 'dark';
  dashboardData?: any;
  onToggleSidebar?: () => void;
  showSidebarToggle?: boolean;
}

const ChemistryCard: React.FC<ChemistryCardProps> = ({
  mode = 'light',
  dashboardData,
  onToggleSidebar,
  showSidebarToggle = true
}) => {
  // Calculate pH color using shared utils (0-14 scale)
  let phColor = '#9CA3AF'; // Default gray
  if (dashboardData?.dashboard?.actual_ph !== undefined) {
    phColor = getPhColor(dashboardData.dashboard.actual_ph / 10);
  }

  // Calculate ORP color using shared utils and desired target
  let orpColor = '#9CA3AF'; // Default gray
  if (dashboardData?.dashboard?.actual_orp !== undefined && dashboardData?.dashboard?.d_orp_mineral) {
    const orpValue = dashboardData.dashboard.actual_orp;
    const desiredValue = dashboardData.dashboard.d_orp_mineral;
    orpColor = getOrpColorByDesired(orpValue, desiredValue, dashboardData.dashboard.is_naked);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-semibold">Chemistry</h5>
        <div className="flex items-center space-x-2">
          {dashboardData?.dashboard?.chemistry_stable !== undefined && (
            <div className={`px-3 py-1 rounded-full text-xs ${dashboardData.dashboard.chemistry_stable
                ? 'bg-emerald-100/80 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                : 'bg-amber-100/80 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
              }`}>
              {dashboardData.dashboard.chemistry_stable ? 'Stable' : 'Unstable'}
            </div>
          )}
          {showSidebarToggle && onToggleSidebar && (
            <button
              onClick={onToggleSidebar}
              className={classNames(
                "p-1.5 rounded-full transition-colors",
                {
                  "hover:bg-gray-100 text-gray-500": mode === 'light',
                  "hover:bg-gray-800 text-gray-400": mode === 'dark'
                }
              )}
              aria-label="View chemistry details"
            >
              <FiChevronRight size={18} />
            </button>
          )}
        </div>
      </div>

      {dashboardData?.dashboard &&
        (dashboardData.dashboard.actual_ph !== undefined ||
          dashboardData.dashboard.actual_orp !== undefined) ? (
        <div className="grid grid-cols-2 gap-4">

          {/* ORP Value Circle */}
          <div className="flex flex-col items-center">
            <div className="relative w-24 h-24 bg-white/50 dark:bg-gray-800/30 rounded-full p-1 backdrop-blur-sm">
              {dashboardData.dashboard.actual_orp !== undefined ? (
                <CircularProgressbarWithChildren
                  value={dashboardData.dashboard.is_naked ? 50 : ((dashboardData.dashboard.actual_orp / (dashboardData.dashboard.d_orp_mineral ? dashboardData.dashboard.d_orp_mineral * 2 : 700)) * 100)}
                  strokeWidth={10}
                  styles={buildStyles({
                    strokeLinecap: 'butt',
                    pathColor: orpColor,
                    trailColor: mode === 'light' ? '#E2E8F0' : '#2D3748',
                    pathTransition: 'stroke-dashoffset 1s ease-in-out',
                    rotation: 0.63,
                    pathTransitionDuration: 1,
                  })}
                  circleRatio={0.75}
                >
                  <div className="flex flex-col items-center justify-center">
                    {dashboardData.dashboard.is_naked ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">-</span>
                    ) : dashboardData.dashboard.main_pump_status === 0 ? (
                      <motion.span
                        className="text-lg font-bold text-gray-500 dark:text-gray-400"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                      >
                        OFF
                      </motion.span>
                    ) : (
                      <>
                        <motion.span
                          className="text-xl font-bold"
                          style={{ color: orpColor }}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5, duration: 0.5 }}
                        >
                          {dashboardData.dashboard.actual_orp}
                        </motion.span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">mV</span>
                      </>
                    )}
                  </div>
                </CircularProgressbarWithChildren>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/70 dark:bg-gray-800/50 rounded-full">
                  <span className="text-sm text-gray-400 dark:text-gray-500">No data</span>
                </div>
              )}
            </div>
            <p className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">ORP Value</p>
            {!dashboardData.dashboard.is_naked && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Desired: {dashboardData.dashboard.d_orp_mineral || 0} mV
              </p>
            )}
          </div>

          {/* pH Value Circle */}
          <div className="flex flex-col items-center">
            <div className="relative w-24 h-24 bg-white/50 dark:bg-gray-800/30 rounded-full p-1 backdrop-blur-sm">
              {dashboardData.dashboard.actual_ph !== undefined ? (
                <CircularProgressbarWithChildren
                  value={(dashboardData.dashboard.actual_ph / 140) * 100}
                  strokeWidth={10}
                  styles={buildStyles({
                    strokeLinecap: 'butt',
                    pathColor: phColor,
                    trailColor: mode === 'light' ? '#E2E8F0' : '#2D3748',
                    pathTransition: 'stroke-dashoffset 1s ease-in-out',
                    rotation: 0.63,
                    pathTransitionDuration: .1,
                  })}
                  circleRatio={0.75}
                >
                  <div className="flex flex-col items-center justify-center">
                    {dashboardData.dashboard.main_pump_status === 0 ? (
                      <motion.span
                        className="text-lg font-bold text-gray-500 dark:text-gray-400"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                      >
                        OFF
                      </motion.span>
                    ) : (
                      <>
                        <motion.span
                          className="text-xl font-bold"
                          style={{ color: phColor }}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5, duration: 0.5 }}
                        >
                          {(dashboardData.dashboard.actual_ph / 10).toFixed(1)}
                        </motion.span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">pH</span>
                      </>
                    )}
                  </div>
                </CircularProgressbarWithChildren>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/70 dark:bg-gray-800/50 rounded-full">
                  <span className="text-sm text-gray-400 dark:text-gray-500">No data</span>
                </div>
              )}
            </div>
            <p className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">pH Value</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Desired: {(dashboardData.dashboard.d_ph / 10).toFixed(1)}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-8 rounded-lg bg-gray-50/70 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 backdrop-blur-sm">
          <FiDroplet className="text-3xl text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-gray-500 dark:text-gray-400">No chemistry data available</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Chemistry readings will appear here once available</p>
        </div>
      )}
    </div>
  );
};

export default ChemistryCard;